import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMixer } from '../context/MixerContext';
import { transformExperience } from '../utils/contentTransform';
import './ExperienceCard.css';

// Projects that have deep-dive content
const hasDeepDive = ['rbc-senior', 'rbc-amplify'];

export default function ExperienceCard({ experience, index, onDeepDive }) {
  const { mixerState } = useMixer();
  const transformed = transformExperience(experience, mixerState);
  
  // Determine which content sections to show based on mixer emphasis
  const showTechEmphasis = transformed.emphasis.technical > 0.6;
  const showBusinessEmphasis = transformed.emphasis.business > 0.6;
  const showLeadershipEmphasis = transformed.emphasis.leadership > 0.6;
  const canDeepDive = hasDeepDive.includes(experience.id);
  
  return (
    <motion.article 
      className={`experience-card ${canDeepDive ? 'experience-card--has-deep-dive' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      layout
    >
      <div className="experience-card__header">
        <div className="experience-card__title-group">
          <h3 className="experience-card__title">{experience.title}</h3>
          <div className="experience-card__company">
            <span className="experience-card__company-name">{experience.company}</span>
            {experience.team && (
              <>
                <span className="experience-card__separator">·</span>
                <span className="experience-card__team">{experience.team}</span>
              </>
            )}
          </div>
        </div>
        <span className="experience-card__period">{experience.period}</span>
      </div>
      
      <div className="experience-card__content">
        <AnimatePresence mode="wait">
          <motion.p 
            key={transformed.displayDescription}
            className="experience-card__description"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {transformed.displayDescription}
          </motion.p>
        </AnimatePresence>
        
        <AnimatePresence mode="wait">
          <motion.div 
            key={transformed.displayRole}
            className="experience-card__role-context"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="experience-card__section">
              <span className="experience-card__section-label">
                {showLeadershipEmphasis ? 'Ownership' : 'Contribution'}
              </span>
              <p className="experience-card__section-text">{transformed.displayRole}</p>
            </div>
          </motion.div>
        </AnimatePresence>
        
        <AnimatePresence>
          {(mixerState.consultingVsEnterprise < 40 || mixerState.consultingVsEnterprise > 60) && (
            <motion.div 
              className="experience-card__context"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="experience-card__section-label">
                {transformed.emphasis.consulting > 0.5 ? 'Operating Context' : 'Enterprise Context'}
              </span>
              <p className="experience-card__section-text experience-card__section-text--muted">
                {transformed.displayContext}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="experience-card__footer">
        <div className="experience-card__technologies">
          {experience.technologies.map(tech => (
            <span 
              key={tech} 
              className="experience-card__tech-tag"
              style={{ 
                opacity: showTechEmphasis ? 1 : 0.6,
                transform: showTechEmphasis ? 'scale(1)' : 'scale(0.95)'
              }}
            >
              {tech}
            </span>
          ))}
        </div>
        
        {showBusinessEmphasis && experience.highlights && (
          <motion.div 
            className="experience-card__highlights"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {experience.highlights.map((highlight, i) => (
              <span key={i} className="experience-card__highlight">
                {highlight}
              </span>
            ))}
          </motion.div>
        )}
        
        {canDeepDive && (
          <button 
            className="experience-card__deep-dive-btn"
            onClick={() => onDeepDive(experience.id)}
          >
            <span>Case Study</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
          </button>
        )}
      </div>
      
      <div 
        className="experience-card__type-indicator"
        data-type={experience.type}
      />
    </motion.article>
  );
}
